/**
 * Visitor analytics tracker for the booking widget.
 *
 * Sends batched events to the tenant's collect endpoint:
 *   https://{tid}.{API host}/api/e   (tour-backend: AnalyticsCollectController)
 *
 * Built to run inside a cross-site iframe on tour operators' websites:
 * - no cookies; visitor id in localStorage, session id in sessionStorage,
 *   in-memory fallback when storage is blocked (Safari / private mode)
 * - text/plain body via sendBeacon / fetch keepalive -> no CORS preflight
 * - flushes when the page is hidden, unloaded, or the widget is closed
 * - nothing is sent until the widget is actually on screen (hidden/preloaded iframes)
 * - disabled when the browser sends Global Privacy Control (Do Not Track is ignored)
 * - tracking errors are swallowed; they must never break the booking flow
 *
 * Records a page view on every route, and every click on every page (buttons, links,
 * inputs, @click elements). `data-track="name"` only gives a click a nicer name.
 * Also adds the visit's ids to booking requests, so paid bookings count as conversions.
 */

import axios from 'axios';

const VISITOR_KEY = 'nat_analytics_vid';
const SESSION_KEY = 'nat_analytics_sid';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_AT_EVENTS = 10;
const MAX_EVENTS_PER_BATCH = 50;
const SCROLL_DEPTHS = [25, 50, 75, 100];

// Route name -> booking funnel step, named like the step bar pages (Tours, Schedule, ...).
const FUNNEL_STEPS = {
    Index: 'tours',
    Init: 'schedule',
    Addons: 'add_ons',
    MyTrip: 'my_trip',
    Maps: 'trip_map',
    Checkout: 'checkout',
};

const memoryStorage = {};
let store = null;
let queue = [];
let seen = false;
let enabled = false;
let scrollDepthsSent = [];
// On the tour list no tour is chosen yet: the store still has the last picked one.
let onTourList = false;

function storageGet(type, key) {
    try {
        return window[type].getItem(key);
    } catch (e) {
        return memoryStorage[type + key] ?? null;
    }
}

function storageSet(type, key, value) {
    try {
        window[type].setItem(key, value);
    } catch (e) {
        memoryStorage[type + key] = value;
    }
}

function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

function visitorId() {
    let id = storageGet('localStorage', VISITOR_KEY);
    if (!id) {
        id = randomId();
        storageSet('localStorage', VISITOR_KEY, id);
    }
    return id;
}

/**
 * Session id. Returns { id, isNew }.
 * After 30 minutes without activity a new visit starts only when the visitor starts again
 * from the tour list, or opens the Thank You page again (startsVisit). Someone who leaves the Schedule page open and later
 * continues to Add-Ons stays in the same visit, so a booking is never split in two and a
 * late scroll/click never creates an empty visit.
 * The break is remembered ("away") until the next tour list view: coming back to the
 * Thank You page redirects to the tour list right away, and that first page view must
 * not hide the break.
 */
function session(startsVisit = false) {
    const now = Date.now();
    let data = null;
    try {
        data = JSON.parse(storageGet('sessionStorage', SESSION_KEY) || 'null');
    } catch (e) {
        data = null;
    }

    const away = !!data && (!!data.away || now - data.last > SESSION_TIMEOUT_MS);
    const isNew = !data || !data.id || (startsVisit && away);
    const id = isNew ? randomId() : data.id;
    storageSet('sessionStorage', SESSION_KEY, JSON.stringify({ id, last: now, away: !isNew && away }));

    return { id, isNew };
}

function inIframe() {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true; // cross-origin access to window.top throws -> we are framed
    }
}

/** Website the widget is embedded on (parent page), when we are in an iframe. */
function parentUrl() {
    if (!inIframe()) {
        return null;
    }
    if (window.location.ancestorOrigins && window.location.ancestorOrigins.length) {
        return window.location.ancestorOrigins[0];
    }
    return origin(document.referrer);
}

// Query parameters that may leave the browser (the backend keeps the same ones, plus utm_*
// for the traffic source). Everything else — e.g. Stripe's payment_intent_client_secret on
// the payment return page, or an email an operator puts in the link — is never sent.
const ALLOWED_QUERY_KEYS = ['oid', 'pid', 'cids', 'aid', 'date', 'utm_source', 'utm_medium', 'utm_campaign'];

/** "/thankyou?payment_intent_client_secret=…&oid=1" -> "/thankyou?oid=1" */
function safePath(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        const kept = new URLSearchParams();
        parsed.searchParams.forEach((value, key) => {
            if (ALLOWED_QUERY_KEYS.includes(key)) {
                kept.append(key, value);
            }
        });
        const query = kept.toString();
        return parsed.pathname + (query ? '?' + query : '');
    } catch (e) {
        return '/';
    }
}

/** "https://site.com/page?x=1" -> "https://site.com" (only the site is stored). */
function origin(url) {
    try {
        return url ? new URL(url).origin : null;
    } catch (e) {
        return null;
    }
}

function utmFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['source', 'medium', 'campaign'].forEach((key) => {
        const value = params.get('utm_' + key);
        if (value) {
            utm[key] = value.slice(0, 150);
        }
    });
    return Object.keys(utm).length ? utm : null;
}

function positiveInt(value) {
    const number = parseInt(value, 10);
    return number > 0 ? number : null;
}

/**
 * Visitor's timezone and language from the browser's own settings (no permission prompt,
 * no geolocation). The backend uses them when the IP gives no country.
 */
function browserLocale() {
    const locale = {};
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) {
            locale.tz = String(tz).slice(0, 64);
        }
    } catch (e) {
        // older browsers: no timezone
    }
    const lang = navigator.language || (navigator.languages && navigator.languages[0]);
    if (lang) {
        locale.lang = String(lang).slice(0, 16);
    }
    return locale;
}

/** https://{tid}.{API host}/api/e — same host the app's API calls use. */
function endpoint() {
    const tenantId = store && store.state.tenantId;
    const apiHost = (process.env.VUE_APP_API_URL || '').split('/')[0];
    if (!tenantId || !apiHost || /^https?:/i.test(process.env.VUE_APP_API_URL || '')) {
        return null;
    }
    return `https://${tenantId}.${apiHost}/api/e`;
}

function send(url, body) {
    try {
        if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) {
            return;
        }
    } catch (e) {
        // fall through to fetch
    }
    try {
        fetch(url, {
            method: 'POST',
            body,
            keepalive: true,
            mode: 'no-cors',
            credentials: 'omit',
            headers: { 'Content-Type': 'text/plain' },
        }).catch(() => {});
    } catch (e) {
        // analytics must never break the widget
    }
}

/** Send queued events now. Safe to call any time (e.g. before closing the widget). */
export function flush() {
    if (!enabled || !seen || !queue.length) {
        return;
    }
    const url = endpoint();
    if (!url) {
        return; // tenant not known yet; keep the queue
    }

    const sessionId = session().id;
    const operator = positiveInt(store.state.tourOperatorId);
    const parent = parentUrl();

    while (queue.length) {
        const events = queue.splice(0, MAX_EVENTS_PER_BATCH);
        const batch = { v: 1, visitor: visitorId(), session: sessionId, sent: Date.now(), events };
        if (operator) {
            batch.operator = operator;
        }
        if (parent) {
            batch.parent = parent;
        }
        Object.assign(batch, browserLocale());
        send(url, JSON.stringify(batch));
    }
}

/**
 * Queue an event. type: page_view | click | scroll | funnel_step | custom
 * data: { path?, name?, step?, tour?, depth?, text?, title?, referrer?, utm? }
 */
export function track(type, data = {}) {
    if (!enabled) {
        return;
    }
    try {
        const event = { type, ts: Date.now(), path: safePath(window.location.pathname + window.location.search), ...data };
        const tour = positiveInt(data.tour !== undefined ? data.tour : !onTourList && store && store.state.packageId);
        if (tour) {
            event.tour = tour;
        } else {
            delete event.tour;
        }
        queue.push(event);
        if (queue.length >= FLUSH_AT_EVENTS) {
            flush();
        }
    } catch (e) {
        // ignore
    }
}

function trackPageView(to) {
    onTourList = FUNNEL_STEPS[to.name] === 'tours';
    // Thank You ends a booking: opening it again after a break is not a continuation of
    // that visit (it would stretch the booked visit into another day).
    const { isNew } = session(onTourList || to.name === 'Thankyou');
    const data = { path: safePath(to.fullPath), title: to.name || undefined };

    // Traffic source only matters for the first page of a visit. Inside the iframe
    // document.referrer is the operator's page, so it is sent as `parent` instead.
    if (isNew) {
        if (!inIframe() && document.referrer) {
            data.referrer = origin(document.referrer);
        }
        const utm = utmFromUrl();
        if (utm) {
            data.utm = utm;
        }
    }

    track('page_view', data);

    const step = FUNNEL_STEPS[to.name];
    if (step) {
        track('funnel_step', { path: safePath(to.fullPath), step });
    }
    scrollDepthsSent = [];
}

/** "Book Now →" -> "book_now"; '' when nothing usable is left. */
function slug(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60);
}

/** Visible text only when it cannot hold personal data (names, emails, phone/booking numbers). */
function safeText(el) {
    const text = (el.innerText || el.value || '').trim().replace(/\s+/g, ' ');
    if (!text || text.length > 40 || /[@\d]/.test(text) || ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName) && el.type !== 'submit' && el.type !== 'button') {
        return '';
    }
    return text;
}

// Tailwind / layout utility classes say nothing about what was clicked.
const UTILITY_CLASS = /[:[\]/]|^(flex|grid|block|inline|hidden|contents|absolute|relative|fixed|sticky|static|truncate|underline|uppercase|capitalize|shadow|rounded|border|transition|transform|outline|group|peer|container|(w|h|min|max|p[xytrbl]?|m[xytrbl]?|gap|space|text|bg|font|leading|tracking|items|justify|self|content|place|order|col|row|z|top|left|right|bottom|inset|overflow|opacity|cursor|shadow|rounded|border|duration|ease|scale|translate|rotate|fill|stroke|object|whitespace|break|list|sm|md|lg|xl|hover|focus|active|disabled|ring|divide|aspect|basis|grow|shrink|flex|grid|line|decoration|pointer|select|resize|appearance|sr)-)/;

function meaningfulClass(el) {
    return (el.getAttribute('class') || '').split(/\s+/).find((name) => name && !UTILITY_CLASS.test(name)) || '';
}

/**
 * Name for a clicked element: data-track wins; otherwise built from id / aria-label /
 * title / name / safe text, prefixed with the element kind (e.g. "button:continue").
 */
/** Text of the <label> for a form field ("Photography Package *" -> "Photography Package"), or ''. */
function fieldLabel(el) {
    let label = null;
    try {
        label = (el.id && document.querySelector('label[for="' + CSS.escape(el.id) + '"]')) || el.closest('label');
    } catch (e) {
        label = null;
    }
    const text = label ? (label.innerText || '').replace(/\*/g, '').replace(/[$€£₹]?\s?\d[\d.,]*/g, '').replace(/\s+/g, ' ').trim() : '';
    return text && text.length <= 60 && !/@/.test(text) ? text : '';
}

/**
 * Name for a clicked element: data-track wins; add-on fields become "addon:<add-on name>:guest_<n>";
 * otherwise built from label / aria-label / title / name / safe text, prefixed with the element kind.
 * Technical ids with numbers (e.g. "field-12-0") are never shown.
 */
function clickName(el) {
    const tagged = el.getAttribute('data-track');
    if (tagged) {
        return tagged;
    }

    // Add-on inputs: id "field-<add-on field id>-<guest index>" (or "-p<index>" for child options).
    const addon = /^field-\d+(?:-p?(\d+))?$/.exec(el.id || '');
    if (addon) {
        const name = slug(fieldLabel(el)) || 'option';
        return ('addon:' + name + (addon[1] !== undefined ? ':guest_' + (Number(addon[1]) + 1) : '')).slice(0, 100);
    }

    const kind = el.tagName === 'A' ? 'link'
        : el.tagName === 'INPUT' ? 'input_' + (el.type || 'text')
        : el.tagName.toLowerCase() === 'button' || el.getAttribute('role') === 'button' ? 'button'
        : el.tagName.toLowerCase();
    const id = /\d/.test(el.id || '') ? '' : el.id;
    const formLabel = ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName) ? fieldLabel(el) : '';
    const label = slug(formLabel) || slug(id) || slug(el.getAttribute('aria-label')) || slug(el.getAttribute('title'))
        || slug(el.getAttribute('name')) || slug(safeText(el)) || slug(meaningfulClass(el));
    return (kind + (label ? ':' + label : '')).slice(0, 100);
}

// Every clickable element on every page (Vue @click divs mostly carry cursor-pointer).
const CLICKABLE = '[data-track], a, button, [role="button"], input[type="button"], input[type="submit"],'
    + ' input[type="checkbox"], input[type="radio"], select, summary, .cursor-pointer';

function onClick(e) {
    const el = e.target && e.target.closest ? e.target.closest(CLICKABLE) : null;
    if (!el) {
        return;
    }
    const data = { name: clickName(el) };
    const text = safeText(el);
    if (text) {
        data.text = text;
    }
    const tour = positiveInt(el.getAttribute('data-track-tour'));
    if (tour) {
        data.tour = tour;
    }
    track('click', data);
}

function onScroll() {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) {
        return;
    }
    const percent = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
    SCROLL_DEPTHS.forEach((depth) => {
        if (percent >= depth && !scrollDepthsSent.includes(depth)) {
            scrollDepthsSent.push(depth);
            track('scroll', { depth });
        }
    });
}

/** Wait until the widget is on screen (a preloaded / hidden iframe is not a visit). */
function whenSeen() {
    const markSeen = () => {
        if (!seen) {
            seen = true;
            flush();
        }
    };
    if (!('IntersectionObserver' in window)) {
        markSeen();
        return;
    }
    const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            markSeen();
        }
    });
    observer.observe(document.documentElement);
    // Any interaction also proves the widget is visible.
    ['pointerdown', 'keydown'].forEach((type) => window.addEventListener(type, markSeen, { once: true, passive: true }));
}

/**
 * Global Privacy Control only. Do Not Track is deprecated, has no legal weight, and Safari
 * removed it — honouring it hid Chrome visitors (DNT setting on) while Safari was counted.
 */
function privacyOptOut() {
    return navigator.globalPrivacyControl === true;
}

// Booking requests that create/confirm a paid booking. create-pre-booking is stored whole
// by the backend, so the pre-booking:verify recovery job also gets the ids.
const BOOKING_REQUEST = /\/(create-pre-booking|package-booking-tour|package-booking-3ds-payment)$/;

/**
 * Adds analytics_session / analytics_visitor to booking requests, so the backend can link
 * its `booking_confirmed` event to this visit (AnalyticsConversionRecorder).
 */
function attachToBookingRequests() {
    axios.interceptors.request.use((config) => {
        try {
            const isPlainObject = config.data && Object.prototype.toString.call(config.data) === '[object Object]';
            if ((config.method || '').toLowerCase() === 'post' && BOOKING_REQUEST.test(config.url || '') && isPlainObject) {
                config.data = { ...config.data, analytics_session: session().id, analytics_visitor: visitorId() };
                flush(); // send the checkout events before the booking is recorded
            }
        } catch (e) {
            // never block a booking request
        }
        return config;
    });
}

/** Install once from main.js: installTracker({ router, store }). */
export function installTracker(options) {
    try {
        if (privacyOptOut() || typeof window === 'undefined') {
            return;
        }
        store = options.store;
        enabled = true;

        // A failed or duplicate navigation (e.g. clicking the current step) did not show a page.
        options.router.afterEach((to, from, failure) => {
            if (!failure) {
                trackPageView(to);
            }
        });

        document.addEventListener('click', onClick, { capture: true, passive: true });
        window.addEventListener('scroll', onScroll, { passive: true });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                flush();
            }
        });
        window.addEventListener('pagehide', flush);
        setInterval(flush, FLUSH_INTERVAL_MS);

        attachToBookingRequests();
        whenSeen();
    } catch (e) {
        enabled = false;
    }
}

export default { installTracker, track, flush };
